import { useEffect, useState, useCallback } from 'react'
import { api } from '../lib/api'
import type { User, PaginatedResult } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { Drawer } from '../components/ui/Drawer'
import { StatusBadge } from '../components/ui/StatusBadge'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

const ROLES = ['super_admin', 'admin', 'manager', 'technician', 'viewer'] as const

export default function Users() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editUser, setEditUser] = useState<User | null>(null)
  const [formData, setFormData] = useState({ email: '', full_name: '', role: 'viewer' as string, phone: '', hourly_rate: '' })
  const [saving, setSaving] = useState(false)

  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' })
      if (search) params.set('search', search)
      const result = await api.get<PaginatedResult<User>>(`/users?${params}`)
      setUsers(result.data)
      setTotalPages(result.totalPages)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [page, search])

  useEffect(() => { loadUsers() }, [loadUsers])

  function openCreate() {
    setEditUser(null)
    setFormData({ email: '', full_name: '', role: 'viewer', phone: '', hourly_rate: '' })
    setDrawerOpen(true)
  }

  function openEdit(user: User) {
    setEditUser(user)
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      phone: user.phone ?? '',
      hourly_rate: user.hourly_rate != null ? String(user.hourly_rate) : '',
    })
    setDrawerOpen(true)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        email: formData.email,
        full_name: formData.full_name,
        role: formData.role,
        phone: formData.phone || undefined,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : undefined,
      }

      if (editUser) {
        await api.patch(`/users/${editUser.slug}`, payload)
      } else {
        await api.post('/users', payload)
      }
      setDrawerOpen(false)
      setEditUser(null)
      loadUsers()
    } catch { /* handled */ }
    finally { setSaving(false) }
  }

  async function toggleActive(user: User) {
    try {
      await api.patch(`/users/${user.slug}`, { is_active: user.is_active ? 0 : 1 })
      loadUsers()
    } catch { /* handled */ }
  }

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title" style={{ fontSize: 22 }}>Users</h1>
        <Button icon={PlusIcon} onClick={openCreate}>Add User</Button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          className="input max-w-xs"
          style={{ fontSize: 14 }}
        />
      </div>

      {loading ? (
        <TableSkeleton rows={5} cols={6} />
      ) : users.length === 0 ? (
        <EmptyState
          title="No users found"
          description={search ? 'Try a different search term.' : 'Add your first team member.'}
          action={!search ? <Button onClick={openCreate}>Add User</Button> : undefined}
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="table-wrapper hidden sm:block">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-left">Name</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-left">Email</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-left">Role</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-right">Rate</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-center">Status</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                          style={{ background: 'var(--primary-soft)' }}
                        >
                          <span style={{ color: 'var(--primary)', fontSize: 12, fontWeight: 500 }}>
                            {user.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{user.full_name}</span>
                      </div>
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{user.email}</td>
                    <td>
                      <Badge v="info">{user.role.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td className="text-right mono" style={{ fontSize: 14, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
                      {user.hourly_rate != null ? `$${user.hourly_rate}/hr` : '—'}
                    </td>
                    <td className="text-center">
                      <StatusBadge status={user.is_active ? 'active' : 'inactive'} />
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(user)}
                          style={{ fontSize: 12, color: 'var(--primary)' }}
                          className="hover:underline"
                        >
                          Edit
                        </button>
                        <span style={{ color: 'var(--text-tertiary)' }}>·</span>
                        <button
                          onClick={() => toggleActive(user)}
                          style={{ fontSize: 12, color: user.is_active ? 'var(--error)' : 'var(--success)' }}
                          className="hover:underline"
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="sm:hidden space-y-2">
            {users.map((user) => (
              <div key={user.id} className="mobile-card" onClick={() => openEdit(user)}>
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: 'var(--primary-soft)' }}
                  >
                    <span style={{ color: 'var(--primary)', fontSize: 14, fontWeight: 500 }}>
                      {user.full_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{user.full_name}</p>
                    <p className="truncate" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{user.email}</p>
                  </div>
                  <div className="text-right">
                    <Badge v="info">{user.role.replace(/_/g, ' ')}</Badge>
                    <StatusBadge status={user.is_active ? 'active' : 'inactive'} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Page {page} of {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => { setDrawerOpen(false); setEditUser(null) }} title={editUser ? 'Edit User' : 'Add User'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Full Name *</label>
            <input
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="input"
              required
              placeholder="John Smith"
              style={{ fontSize: 14 }}
            />
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
              required
              placeholder="john@example.com"
              style={{ fontSize: 14 }}
            />
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="input"
              style={{ fontSize: 14 }}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Phone</label>
            <input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="input"
              placeholder="(555) 123-4567"
              style={{ fontSize: 14 }}
            />
          </div>
          <div>
            <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Hourly Rate ($)</label>
            <input
              type="number"
              step="0.01"
              value={formData.hourly_rate}
              onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
              className="input"
              placeholder="75.00"
              style={{ fontSize: 14 }}
            />
          </div>
          {!editUser && (
            <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>A temporary password will be generated. The user can change it after logging in.</p>
          )}
          <Button type="submit" loading={saving} className="w-full">
            {editUser ? 'Save Changes' : 'Create User'}
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
